/**
 * <s-image-magnifier> — Hover magnifier for the product gallery (desktop, mouse only).
 *
 * Markup contract:
 *   Placed inside a positioned gallery wrapper, after the [data-gallery-track] element.
 *   Children: [data-magnifier-lens] (rectangle over the image) and [data-magnifier-pane]
 *   (enlarged view shown to the right of the gallery, over the product info column).
 *   Slides marked [data-media-slide][data-zoom-src] are magnifiable; video slides are skipped.
 *
 * Behavior:
 *   Moving the mouse over an image slide shows the lens and a 2.5x view of that spot
 *   in the pane. Leaving the image, scrolling, or resizing hides both.
 *   Click still opens the full-screen zoom dialog.
 */
const ZOOM = 2.5;
const GAP = 48; // Matches md:gap-12 between the gallery and the product info column.
const EDGE = 16; // Breathing room between the enlarged view and the right edge of the window.

class SImageMagnifier extends HTMLElement {
  #ac;
  #slide = null;
  #natural = new Map();

  connectedCallback() {
    const host = this.parentElement;
    const track = host?.querySelector('[data-gallery-track]');
    const lens = this.querySelector('[data-magnifier-lens]');
    const pane = this.querySelector('[data-magnifier-pane]');
    if (!host || !track || !lens || !pane) return;

    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const desktop = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');

    const hide = () => {
      this.#slide = null;
      this.hidden = true;
    };

    const loadNatural = (src) => {
      if (this.#natural.has(src)) return this.#natural.get(src);
      const entry = { w: 0, h: 0 };
      this.#natural.set(src, entry);
      const img = new Image();
      img.onload = () => {
        entry.w = img.naturalWidth;
        entry.h = img.naturalHeight;
      };
      img.src = src;
      return entry;
    };

    const update = (event) => {
      if (!desktop.matches) return hide();
      const slide = event.target.closest?.('[data-media-slide]');
      const src = slide?.dataset.zoomSrc;
      if (!slide || !src || !track.contains(slide)) return hide();

      const box = slide.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const x = event.clientX - box.left;
      const y = event.clientY - box.top;
      if (x < 0 || y < 0 || x > box.width || y > box.height) return hide();

      // Pane fills the space to the right of the gallery, up to the right edge of the window.
      const viewportRight = document.documentElement.clientWidth - EDGE;
      const paneW = Math.max(240, viewportRight - box.right - GAP);
      const paneH = box.height;

      if (this.#slide !== slide) {
        this.#slide = slide;
        pane.style.backgroundImage = `url("${src.replace(/"/g, '%22')}")`;
      }

      // Image is drawn with object-fit: cover; mirror that geometry for the enlarged view.
      const natural = loadNatural(src);
      const nw = natural.w || box.width;
      const nh = natural.h || box.height;
      const scale = Math.max(box.width / nw, box.height / nh);
      const coverW = nw * scale;
      const coverH = nh * scale;
      const offX = (box.width - coverW) / 2;
      const offY = (box.height - coverH) / 2;

      const lensW = Math.min(box.width, paneW / ZOOM);
      const lensH = Math.min(box.height, paneH / ZOOM);
      const lensX = Math.min(Math.max(x - lensW / 2, 0), box.width - lensW);
      const lensY = Math.min(Math.max(y - lensH / 2, 0), box.height - lensH);

      lens.style.width = `${lensW}px`;
      lens.style.height = `${lensH}px`;
      lens.style.transform = `translate(${box.left - hostRect.left + lensX}px, ${box.top - hostRect.top + lensY}px)`;

      pane.style.width = `${paneW}px`;
      pane.style.height = `${paneH}px`;
      pane.style.left = `${box.right - hostRect.left + GAP}px`;
      pane.style.top = `${box.top - hostRect.top}px`;
      pane.style.backgroundSize = `${coverW * ZOOM}px ${coverH * ZOOM}px`;
      pane.style.backgroundPosition = `${(offX - lensX) * ZOOM}px ${(offY - lensY) * ZOOM}px`;

      this.hidden = false;
    };

    track.addEventListener('mousemove', update, { signal });
    track.addEventListener('mouseleave', hide, { signal });
    track.addEventListener('scroll', hide, { signal, passive: true });
    window.addEventListener('resize', hide, { signal });
    window.addEventListener('scroll', hide, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-image-magnifier')) {
  customElements.define('s-image-magnifier', SImageMagnifier);
}
